import { useCallback, useEffect, useState } from "react";
import PayeeDisbursementsService from "../../../../../../../../../services/accounts/payeeDisbutsements";
import { FormInstance } from "antd";
import { DISBURSEMENTS_STATUS } from "../../../../../../../../../lib/constants";
import dayjs from "dayjs";
import { useTransformRequest } from "./useTransformRequest";
import { TPayeeStatusState } from "../types";

export const useGetInitialValues = (
  id: string | null,
  form: FormInstance,
  setDisbursementMethodState: React.Dispatch<
    React.SetStateAction<{
      isEscrowAcc: boolean;
      isACHMethod: boolean;
    }>
  >,
  setPayeeStatusState: React.Dispatch<React.SetStateAction<TPayeeStatusState>>,
  isPassthru: boolean,
) => {
  const [isLoading, setIsLoading] = useState<boolean>(!!id);

  const { transform } = useTransformRequest(false);

  const getAmountSwitcherValue = useCallback(
    (amount: number | null, percentage: number | null, type: number): 2 | 1 => {
      if ([0, 7].includes(type)) {
        return 2;
      }

      if (!isPassthru && [5, 6].includes(type)) {
        return 1;
      }

      if (!amount && !percentage) {
        return 1;
      }

      return percentage || !amount ? 2 : 1;
    },
    [isPassthru],
  );

  const fetchData = useCallback(() => {
    if (id !== null) {
      PayeeDisbursementsService.fetch(id)
        .then((res: any) => {
          const initialValues = {
            ...res,
            type: res.type === 101 ? "Impound Distribution" : res.type,
            beginningDate:
              res.beginningDate !== null ? dayjs(res.beginningDate) : null,
            endDate: res.endDate !== null ? dayjs(res.endDate) : null,
            amountSwitcher: getAmountSwitcherValue(
              res.amount,
              res.percentage,
              res.type,
            ),
            status:
              res.status === 3 ? DISBURSEMENTS_STATUS[0].value : res.status,
          };

          const { percentage } = initialValues;

          if (initialValues.percentage) {
            initialValues.percentage = percentage.toFixed(4);
          }
          transform(initialValues);

          setIsLoading(false);

          form.setFieldsValue(initialValues);

          setDisbursementMethodState({
            isEscrowAcc: res.method === 8,
            isACHMethod: res.method >= 5 && res.method <= 7,
          });

          const amount =
            initialValues.amountSwitcher === 1 ? res.amount : res.percentage;

          setPayeeStatusState({
            isActive: initialValues.status === 1,
            isAmountNonNullOrZero: amount !== null && amount > 0,
            isEnableForPayments: initialValues.enableForPayments === true,
          });
        })
        .catch(() => setIsLoading(false));
    }
  }, [
    id,
    getAmountSwitcherValue,
    transform,
    form,
    setDisbursementMethodState,
    setPayeeStatusState,
  ]);

  useEffect(() => fetchData(), [fetchData]);

  return { isLoading };
};
